<template>
    <div
        class="top-0 h-screen w-screen bg-ds-primary-100"
        :class="{ fixed: !isIntegrationRoute }"
    >
        <div
            class="top-nav-wrapper"
            :class="{ 'max-legacy-md:translate-x-3/4': companyNavigationStore.showSideNav }"
        >
            <SideNavToggleButton
                v-if="hasSideNav"
                :class="{ hidden: isLoginRoute || isIntegrationRoute }"
                @click="companyNavigationStore.toggleSideNav"
            />
            <div class="flex-1">
                <slot name="top">
                    <RouterView name="topNav" />
                </slot>
            </div>
        </div>
        <div class="top-14 flex w-full">
            <SideNavPanel
                v-if="hasSideNav"
                class="left-0 top-0 max-legacy-md:fixed max-legacy-md:h-dvh"
            >
                <RouterView name="sideNav" />
            </SideNavPanel>

            <!-- total width minus fixed header rem size - max-h viewport height minus TopNav -->
            <div
                class="layout-content"
                :class="{
                    'max-legacy-md:translate-x-3/4 legacy-md:w-[calc(100%-14rem)]': companyNavigationStore.showSideNav,
                    'max-h-[calc(100vh-70px)]': !isIntegrationRoute,
                }"
            >
                <div
                    v-if="hasTitleNav || hasHeader"
                    class="w-full"
                    :class="['bg-white pt-8', !hasTabNav && 'border-b border-ds-primary-200 pb-8']"
                >
                    <RouterView name="header" />

                    <RouterView
                        class="mt-6"
                        :hasEarlyDepositAccess
                        :hasPostCloseAccess
                        name="tabNav"
                    />
                </div>
                <div
                    class="flex gap-6"
                    :class="{
                        'legacy-md:pl-0': isLoginRoute,
                        'overflow-y-scroll legacy-md:pl-8': !isLoginRoute,
                        'content-with-inner-side-nav': hasInnerSideNav,
                        'base-content-spacing': !hasInnerSideNav && hasBaseContentSpacing,
                    }"
                >
                    <RouterView name="innerSideNav" />

                    <div
                        class="w-full flex-1"
                        :class="{
                            'min-h-[calc(100vh-200px)]': !isIntegrationRoute,
                        }"
                    >
                        <div
                            class="flex flex-col"
                            :class="{ 'min-h-[calc(100vh-164px)]': !isIntegrationRoute }"
                        >
                            <div :class="{ 'min-h-[calc(100vh-56px)]': !isIntegrationRoute }">
                                <div
                                    :class="{
                                        'min-h-[calc(100vh-40vh)]': !isLoginRoute,
                                        'min-h-screen': isIntegrationRoute,
                                    }"
                                >
                                    <div
                                        class="flex items-center justify-center gap-6 align-top"
                                        :class="{
                                            'min-h-[calc(100vh-92px-128px)]': isLoginRoute && !isRoot,
                                            'w-100': !isLoginRoute,
                                        }"
                                    >
                                        <slot name="content">Loading content</slot>
                                    </div>
                                </div>

                                <div>
                                    <slot name="footer"></slot>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import SideNavPanel from '@/components/Navigation/side-nav/SideNavPanel.vue';
import SideNavToggleButton from '@/components/Navigation/side-nav/SideNavToggleButton.vue';
import { INTEGRATION_ROUTES_INJECTION_KEY, NO_SIDEBAR_ROUTES_INJECTION_KEY } from '@/injection-keys';
import type { CompanyPortalLaravelLayoutProps } from '@/layouts/company-portal-layout.types';
import { useCompanyNavigationStore } from '@/stores/company/company-navigation.store';
import { computed, inject } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const hasInnerSideNav = computed(() => !!route.matched?.find(match => !!match?.components?.innerSideNav));
const hasHeader = computed(() => !!route.matched?.find(match => !!match?.components?.header));
const hasTabNav = computed(() => !!route.matched?.find(match => !!match?.components?.tabNav));

const companyNavigationStore = useCompanyNavigationStore();

defineProps<CompanyPortalLaravelLayoutProps>();

let hasSideNav: boolean = true;
let hasTitleNav: boolean = true;
let isRoot: boolean = false;
let isIntegrationRoute: boolean = false;
let isLoginRoute: boolean = false;

// used to hide the sideNav and tabNav on login pages
// the vue-router was returning the wrong path ('/') on page load so we used window.location.pathname here

const noSidebarRoutes = inject(NO_SIDEBAR_ROUTES_INJECTION_KEY, []);
const integrationRoutes = inject(INTEGRATION_ROUTES_INJECTION_KEY, []);

if (window.location.pathname === '/') {
    hasSideNav = false;
    hasTitleNav = false;
    isRoot = true;
    isLoginRoute = true;
}

if (noSidebarRoutes.some(route => window.location.pathname.includes(route))) {
    hasSideNav = false;
    hasTitleNav = false;
    isLoginRoute = true;
}

if (integrationRoutes.some(route => window.location.pathname.includes(route))) {
    hasSideNav = false;
    hasTitleNav = false;
    isIntegrationRoute = true;
}

const hasBaseContentSpacing = (!isRoot && !isIntegrationRoute) || !isLoginRoute;
</script>

<style scoped lang="scss">
.top-nav-wrapper {
    @apply flex min-h-14 w-full items-center gap-2 bg-ds-primary-100 transition-transform duration-200 ease-in-out;
}

.layout-content {
    @apply flex flex-1 flex-grow flex-col overflow-x-auto bg-white transition-transform duration-200 ease-in-out xs:left-0 xs:w-full;
}

.base-content-spacing {
    @apply pt-3;
}

.content-with-inner-side-nav {
    @apply pt-14;
}
</style>
